import React, { createContext, useContext, useState, ReactNode } from 'react';

// Define the job details interface
interface JobDetails {
    title?: string;
    company?: string;
    location?: string;
    description?: string;
    salary?: string;
    currency?: string;
    salaryPer?: string;
    type?: string;
    experience?: string | null;
    noticePeriod?: string;
}

// Define the context interface
interface JobDetailsContextType {
    jobDetails: JobDetails | null;
    setJobDetails: (details: JobDetails | null) => void;
    primaryRequirements: string[];
    secondaryRequirements: string[];
    setPrimaryRequirements: React.Dispatch<React.SetStateAction<string[]>>;
    setSecondaryRequirements: React.Dispatch<React.SetStateAction<string[]>>
}

// Create the context with default values
const JobDetailsContext = createContext<JobDetailsContextType | undefined>(undefined);

// Create a provider component
interface JobDetailsProviderProps {
    children: ReactNode;
}

export const JobDetailsProvider: React.FC<JobDetailsProviderProps> = ({ children }) => {
    const [jobDetails, setJobDetails] = useState<JobDetails | null>({
        title: "",
        company: "",
        location: "",
        description: "",
        salary: "",
        currency: "USD",
        salaryPer: "Year",
        type: "",
        experience: null,
        noticePeriod: ""
    });
    const [primaryRequirements, setPrimaryRequirements] = useState<string[]>([])
    const [secondaryRequirements, setSecondaryRequirements] = useState<string[]>([])
    const value = {
        jobDetails,
        setJobDetails,
        primaryRequirements,
        setPrimaryRequirements,
        secondaryRequirements,
        setSecondaryRequirements
    };

    return (
        <JobDetailsContext.Provider value={value}>
            {children}
        </JobDetailsContext.Provider>
    );
};

// Create a custom hook to use the context
export const useJobDetails = () => {
    const context = useContext(JobDetailsContext);
    if (context === undefined) {
        throw new Error('useJobDetails must be used within a JobDetailsProvider');
    }
    return context;
};